import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { NodeData } from '@/types/workflow';
import { Play, Copy, Trash2 } from 'lucide-react';

interface ManualTriggerNodeProps extends NodeProps {
  data: NodeData;
}

export const ManualTriggerNode = memo(({ data, selected, id }: ManualTriggerNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#22c55e'; // Green for triggers

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[300px] backdrop-blur-md bg-background/75 border-green-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-green-500/50 shadow-lg shadow-green-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Play className="w-4 h-4 text-green-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || 'Manual Trigger'}</div>
              <div className="text-xs text-muted-foreground">Start workflow manually</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          <div>
            <Label className="text-xs">Trigger Name</Label>
            <Input
              placeholder="e.g., Test Lead Processing"
              value={data.triggerName || ''}
              onChange={(e) => updateNodeData({ triggerName: e.target.value })}
              className="h-8"
            />
          </div>

          <div>
            <Label className="text-xs">Initial Data (for testing)</Label>
            <Textarea
              placeholder={`{\n  "name": "John Doe",\n  "email": "john@example.com",\n  "company": "Acme Corp"\n}`}
              value={data.initialData || ''}
              onChange={(e) => updateNodeData({ initialData: e.target.value })}
              className="min-h-[80px] resize-none text-xs font-mono"
            />
          </div>

          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              placeholder="Describe what this trigger starts or what data it provides..."
              value={data.description || ''}
              onChange={(e) => updateNodeData({ description: e.target.value })}
              className="min-h-[50px] resize-none text-xs"
            />
          </div>

          <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-800">
            <div className="text-xs text-green-700 dark:text-green-300">
              ▶️ Click "Run Workflow" to start this workflow manually with the test data
            </div>
          </div>
        </CardContent>
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="trigger"
          className="custom-handle"
          style={{
            backgroundColor: '#22c55e',
            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

ManualTriggerNode.displayName = 'ManualTriggerNode';